import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  closestGuesses, findCuisine, findMeal, formatMoney, formatPoints, formatRating,
  MIN_PARTICIPANTES, podeRevelarPalpites, pointsLabel, resolveConfig,
  restamEstimativas, RULES_VERSION, storedPlatePoints,
  APPS_DE_DELIVERY, CANAIS, lerPalpite, missoesQueOPratoAjuda, TETO_DO_PALPITE,
  interacaoDe, podeInteragir,
} from "@nhomi/core";
import { nomeDaSessao, useSession } from "@/lib/auth";
import { watchChallenge, type Challenge } from "@/data/challenges";
import {
  deletePlate, fotosDoPrato, plateDate, photoURL, temFoto, toCorePlate, watchPlate,
  type Plate as PlateT,
} from "@/data/plates";
import { watchMembers, type Member } from "@/data/members";
import {
  comment, guess, rate, reagir, watchComments, watchGuesses, watchMyRating,
  watchMyReaction, type Comment, type Guess,
} from "@/data/social";
import {
  corrigirCalorias, cotaDaPessoaAcabou, estimarCalorias, formatKcal, FONTE,
  limparRecado, watchUsoDeHoje, type Estimativa,
} from "@/data/kcal";
import { useMe, usePlano } from "@/lib/plano";
import { usePedidoDeEstimativa } from "@/lib/estimativa";
import { CotaDeCaloria } from "@/ui/CotaDeCaloria";
import { bloquear } from "@/data/moderacao";
import { useBloqueados, semBloqueados } from "@/lib/bloqueios";
import { Denunciar, type Alvo } from "@/ui/Denunciar";
import { compartilharFoto, podeCompartilhar } from "@/lib/nativo";
import { useToqueLongo } from "@/lib/toque";
import { Anuncio } from "@/ui/Anuncio";
import { BarraDeReacao, ResumoDeReacoes } from "@/ui/Reacoes";
import { Avatar, Empty, Spinner, TopBar } from "@/ui/primitives";
import {
  IconBlock, IconChevron, IconDots, IconFlag, IconPencil, IconPin, IconShare,
  IconTrash,
} from "@/ui/icons";
import { Toast } from "@/ui/Toast";
import { LogoDoApp } from "@/ui/LogoDoApp";

/* ============================================================
   O prato aberto

   A tela mais visitada do app. Estrutura copiada do GymEats: foto,
   autor, título, descrição, marcas, nota, palpite de preço, comentários.

   Duas diferenças de fundo, nenhuma delas visual:

   - a foto é `object-fit: contain` sobre preto, não `cover`. Prato
     fotografado na vertical estava sendo cortado ao meio;
   - a nota é uma subcoleção que só o dono lê, não um mapa dentro do
     prato. Lá o anonimato era convenção de tela; aqui é regra do banco.
   ============================================================ */

/** "20 de agosto às 16:32" */
const quando = (d: Date) =>
  d.toLocaleString("pt-BR", {
    day: "numeric", month: "long", hour: "2-digit", minute: "2-digit",
  }).replace(",", " às");

const desde = (d: Date) => {
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "agora";
  if (s < 3600) return `${Math.floor(s / 60)} min`;
  if (s < 86400) return `${Math.floor(s / 3600)} h`;
  if (s < 604800) return `${Math.floor(s / 86400)} d`;
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
};

export function Plate() {
  const { cid = "", pid = "" } = useParams();
  const navigate = useNavigate();
  const { user } = useSession();
  const [plate, setPlate] = useState<PlateT | null | undefined>(undefined);
  const [challenge, setChallenge] = useState<Challenge | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [minhaNota, setMinhaNota] = useState<number | null>(null);
  const [palpites, setPalpites] = useState<Guess[]>([]);
  const [comentarios, setComentarios] = useState<Comment[]>([]);
  const [texto, setTexto] = useState("");
  const [menu, setMenu] = useState(false);
  const [palpitesKcal, setPalpitesKcal] = useState<Guess[]>([]);
  const [estimando, setEstimando] = useState(false);
  const [recadoDaIA, setRecadoDaIA] = useState("");
  /* O SERVIDOR mandou dizer que a cota acabou. Vale mais que a conta
     local: o contador da tela pode estar atrás (o teto mudou no painel,
     outro aparelho gastou, duas abas). Sem isto o botão continuava ali
     depois da recusa, convidando pro mesmo "não" de novo — foi o que
     apareceu no teste. */
  const [cotaRecusada, setCotaRecusada] = useState(false);
  const [minhaReacao, setMinhaReacao] = useState<string | null>(null);
  const [denuncia, setDenuncia] = useState<Alvo | null>(null);
  const bloqueados = useBloqueados();
  const [reagindo, setReagindo] = useState(false);
  const [usadasHoje, setUsadasHoje] = useState(0);
  /* `config` nesta tela já é o do DESAFIO (`resolveConfig`), então o do
     app entra com outro nome — os dois no mesmo escopo é como um deles
     vira o outro sem ninguém notar. */
  const { config: configDoApp, plano } = usePlano();
  const me = useMe();
  const [toast, setToast] = useState<string | null>()

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), 2600);
    return () => window.clearTimeout(timer);
  }, [toast]);


  useEffect(() => watchPlate(cid, pid, setPlate), [cid, pid]);
  useEffect(() => watchChallenge(cid, setChallenge), [cid]);
  useEffect(() => watchMembers(cid, setMembers), [cid]);
  useEffect(() => watchComments(cid, pid, setComentarios), [cid, pid]);
  useEffect(() => watchGuesses(cid, pid, "price", setPalpites), [cid, pid]);
  useEffect(() => watchGuesses(cid, pid, "kcal", setPalpitesKcal), [cid, pid]);
  useEffect(() => {
    if (!user) return;
    return watchMyRating(cid, pid, user.uid, setMinhaNota);
  }, [cid, pid, user]);
  useEffect(() => {
    if (!user) return;
    return watchMyReaction(cid, pid, user.uid, setMinhaReacao);
  }, [cid, pid, user]);
  /* Só o autor pede estimativa, então só ele precisa do contador. */
  useEffect(() => {
    if (!user || plate?.uid !== user.uid) return;
    return watchUsoDeHoje(user.uid, setUsadasHoje);
  }, [user, plate?.uid]);

  /* Pedir a estimativa é o mesmo trabalho vindo de dois lugares — o
     botão logo abaixo e o interruptor da tela de publicar —, então é uma
     função só. Duas cópias divergiriam justamente no tratamento de erro,
     que é a parte que ninguém repete à mão ao testar. */
  const pedirEstimativa = useCallback(async () => {
    setEstimando(true);
    setRecadoDaIA("");
    try {
      const r: Estimativa = await estimarCalorias(cid, pid);
      if (!r.ok) setRecadoDaIA(r.detalhe ?? "Não consegui reconhecer comida nessa foto.");
    } catch (err) {
      /* Cota da pessoa não é falha: é o produto funcionando. Ela troca a
         tela inteira pelo aviso, sem recado vermelho por cima — o aviso
         já diz tudo, e dizer duas vezes parece defeito. */
      if (cotaDaPessoaAcabou(err)) {
        setCotaRecusada(true);
      } else {
        /* O `[429]` que o SDK gruda no fim não diz nada pra quem está
           olhando a foto de um ovo, e a mensagem do servidor já explica
           em português — inclusive a da cota da IA, que é nossa e passa
           em minutos. */
        setRecadoDaIA(
          limparRecado((err as Error).message || "") || "Não deu pra estimar.",
        );
      }
    } finally {
      setEstimando(false);
    }
  }, [cid, pid]);

  /* A estimativa pedida lá na tela de publicar. O porquê de ela ser
     disparada AQUI, e não no `Compose`, está em `lib/estimativa.ts`.

     NÃO se confere a cota antes de chamar, de propósito. Quem fecha a
     porta é o interruptor da tela de publicar, que já nasce desligado
     sem cota, e o servidor, que recusa numa transação. Conferir de novo
     aqui usaria um contador que pode estar velho por um segundo — e
     barraria o caminho da BASE DO GRUPO, que responde ANTES do teto e
     não gasta nada: prato repetido continua ganhando caloria de graça. */
  const souOAutor = !!user && !!plate && plate.uid === user.uid;
  /* Sem foto a IA lê a descrição — que a regra obriga a existir. */
  const podeEstimar = !!plate
    && (temFoto(plate) || (plate.description?.trim().length ?? 0) >= 5);
  const semCaloria = !!plate && plate.kcal == null && podeEstimar;
  usePedidoDeEstimativa(souOAutor && semCaloria, pedirEstimativa);

  /* Mesmo gesto do feed: segurar a foto abre a barra. Aqui ele é o único
     jeito de descobrir a reação sem tocar, e por isso o resumo abaixo
     também abre — gesto invisível não pode ser o único caminho. */
  const segurarAFoto = useToqueLongo(() => setReagindo(true));

  /* As fotos grandes e a miniatura da capa são resolvidas em paralelo:
     enquanto a definitiva não chega, a miniatura aparece borrada no lugar
     dela. Sem isso a tela abre com um retângulo preto e parece travada. */
  const [thumb, setThumb] = useState<string | null>(null);
  const [fotos, setFotos] = useState<(string | null)[]>([]);
  const [naFoto, setNaFoto] = useState(0);

  const thumbPath = plate?.thumbPath;
  useEffect(() => { void photoURL(thumbPath).then(setThumb); }, [thumbPath]);

  const caminhos = plate ? fotosDoPrato(plate) : [];
  const chaveDasFotos = caminhos.join("|");
  useEffect(() => {
    const lista = chaveDasFotos ? chaveDasFotos.split("|") : [];
    setNaFoto(0);
    void Promise.all(lista.map((c) => photoURL(c))).then(setFotos);
  }, [chaveDasFotos]);

  const config = useMemo(() => resolveConfig(challenge), [challenge]);
  const nomeDe = (uid: string) => members.find((m) => m.uid === uid)?.name ?? "alguém";

  if (plate === undefined) return <div className="screen center"><Spinner /></div>;
  if (!plate) {
    return (
      <main className="screen">
        <TopBar back={`/c/${cid}`} />
        <Empty emoji="🍽️" title="Prato não encontrado">
          <p>Talvez tenha sido apagado.</p>
        </Empty>
      </main>
    );
  }

  const meu = plate.uid === user?.uid;
  const meal = findMeal(config, plate.mealId);
  const cuisine = findCuisine(config, plate.cuisineId);
  const valeu = storedPlatePoints(config, {
    id: plate.id, uid: plate.uid, day: plate.day, at: 0,
    homemade: plate.homemade, mealId: plate.mealId, cuisineId: plate.cuisineId,
    // Sem era, a de hoje: é o que o servidor faz. Ver `toCorePlate`.
    rulesVersion: plate.rulesVersion ?? RULES_VERSION, storedBase: plate.basePoints,
  }, plate.mealRepeated);
  const semFoto = !temFoto(plate);
  const missoes = plate.mealRepeated ? [] : missoesQueOPratoAjuda(config, toCorePlate(plate));
  const canal = CANAIS.find((c) => c.id === plate.canal);
  const app = canal?.id === "delivery"
    ? APPS_DE_DELIVERY.find((a) => a.id === plate.appDelivery) : undefined;

  const alvo = plate;

  /* Quem pode comentar e dar nota: a escolha do dono no desafio e a
     chave de quem postou. Palpites e reações não passam por aqui. Com a
     conversa desligada, os comentários antigos ficam ESCONDIDOS, não
     apagados — voltam se religar. */
  const interacao = interacaoDe(challenge);
  const fechado = alvo.fechado === true;
  const conversaVisivel = !fechado && interacao !== "ninguem";
  const pode = (acao: "comentar" | "nota") => !!user && !!challenge && podeInteragir({
    interacao, dono: challenge.ownerUid, autor: alvo.uid, eu: user.uid, fechado, acao,
  });
  const podeComentar = pode("comentar");
  const podeNotar = pode("nota");
  // Comentário de bloqueado some junto com o resto do que a pessoa faz.
  const visiveis = semBloqueados(comentarios, bloqueados);

  /* Os dois tipos de palpite contam JUNTOS: quem chutou o preço aparece
     com nome na mesma tela do bloco de caloria, e é a tela inteira que
     entrega quem participou do prato. */
  const revelarPalpites = podeRevelarPalpites(
    alvo.ratingCount,
    [...palpites, ...palpitesKcal].map((g) => g.uid),
  );

  async function apagar() {
    /* Quando o prato vive em mais de um desafio, o aviso precisa dizer
       isso: apagar aqui apaga em todos, e descobrir depois não tem
       desfazer. A cópia sabe pela `origemCid`; o original, pela lista. */
    const emVarios = !!alvo.origemCid || !!alvo.replicas?.length;
    const aviso = emVarios
      ? "Apagar esse prato? Ele some de TODOS os desafios em que foi publicado, e o placar é refeito."
      : "Apagar esse prato? O placar é refeito.";
    if (!confirm(aviso)) return;
    await deletePlate(cid, alvo.id);
    navigate(`/c/${cid}`, { replace: true });
  }

  /* Compartilha a foto que está À VISTA, não a capa: com carrossel, a
     pessoa desliza até a que quer e aperta compartilhar. Mandar a
     primeira seria ignorar o que ela acabou de escolher. */
  async function compartilhar() {
    await compartilharFoto(
      fotos[naFoto] ?? fotos[0] ?? thumb,
      alvo.title || "Prato no Nhomi",
      window.location.href,
    );
  }

  async function enviarComentario() {
    const t = texto.trim();
    if (!t || !user) return;
    setTexto("");
    await comment(cid, pid, {
      uid: user.uid,
      name: me?.name ?? nomeDaSessao(user),
      photo: me?.photo ?? "",
    }, t);
  }

  /* ---------- nota ---------- */

  const cabecalhoNota = (
    <div className="rate-head">
      <div>
        <div className="rate-avg">{formatRating(alvo.ratingAvg)}<span>/10</span></div>
        <div className="rate-count">
          {alvo.ratingCount === 0
            ? "sem notas ainda"
            : `${alvo.ratingCount} ${alvo.ratingCount === 1 ? "nota" : "notas"}`}
        </div>
      </div>
      {meu && <div className="rate-hint">Seu prato</div>}
    </div>
  );

  const blocoNota = !conversaVisivel ? null : meu || !podeNotar ? (
    <div className="card rate-card">
      {cabecalhoNota}
      {!meu && <div className="hint-row">Neste desafio só o dono dá nota.</div>}
    </div>
  ) : (
    <div className="card rate-card">
      {cabecalhoNota}
      <div className="rate-scale">
        {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
          <button key={n}
            className={`rate-dot ${minhaNota === n ? "on" : ""} ${minhaNota && n <= minhaNota ? "under" : ""}`}
            onClick={() => user && void rate(cid, pid, user.uid, n, minhaNota)}>
            {n}
          </button>
        ))}
      </div>
      <div className="hint-row">
        {minhaNota ? "Toque de novo na sua nota pra desfazer." : "Dá tua nota nesse prato."}
      </div>
    </div>
  );

  /* ---------- palpite de preço ---------- */

  const blocoPreco = (() => {
    if (alvo.price == null) return null;
    const preco = alvo.price;
    const meuPalpite = palpites.some((g) => g.uid === user?.uid);
    const r = config.rules;

    if (!meu && !meuPalpite) {
      return (
        <div className="card guess-card">
          <div className="guess-title">💸 Quanto você acha que custou?</div>
          <ChutarPreco onEnviar={(v) => {
            if (user) void guess(cid, pid, user.uid, "price", v);
          }}
          onAviso={setToast}
          />
          <div className="hint-row">
            O preço real só aparece depois do seu chute.{" "}
            {r.guessPrice > 0 && (
              <>Cravar vale <b>{pointsLabel(r.guessPrice)}</b>
                {r.tolPrice > 0 && ` (até ${formatMoney(r.tolPrice)} de margem)`}.</>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="card guess-card">
        <div className="guess-title">💸 Custou <strong>{formatMoney(preco)}</strong></div>
        <ListaDePalpites
          palpites={palpites} real={preco} revelar={revelarPalpites}
          meuUid={user?.uid} nomeDe={nomeDe}
          formatar={formatMoney}
          distancia={(d) => formatMoney(d).replace("R$", "").trim()}
        />
      </div>
    );
  })();

  /* ---------- calorias ---------- */

  const blocoKcal = (() => {
    const r = config.rules;

    /* Sem estimativa ainda: só o autor pode pedir. Deixar qualquer um
       pedir seria deixar qualquer um revelar o gabarito para si mesmo
       antes de chutar. */
    if (alvo.kcal == null) {
      if (!meu) return null;

      /* Cota acabar não é erro: é o produto funcionando como combinado.
         Por isso a tela DIZ ANTES quantas restam, e quando acabam troca o
         botão por um estado calmo em vez de deixar a pessoa apertar algo
         que já se sabe que vai falhar. */
      const restam = restamEstimativas(configDoApp, plano, usadasHoje);
      /* `||` e não só a conta local: quem tem a palavra final é o
         servidor, e ele já respondeu. */
      const acabou = restam === 0 || cotaRecusada;

      return (
        <div className="card guess-card">
          <div className="guess-title">🔥 Calorias</div>
          <div className="hint-row" style={{ paddingTop: 0 }}>
            {!podeEstimar
              ? "Sem foto nem descrição não dá pra estimar."
              : semFoto
                ? "Sem foto, a IA lê a sua descrição e chuta. Depois você corrige se ela errar."
                : "A IA olha a foto e chuta. Depois você corrige se ela errar — e a galera pode tentar adivinhar antes."}
          </div>
          {recadoDaIA && <div className="hint-row erro">{recadoDaIA}</div>}

          {/* `!estimando` porque a função soma o contador NO COMEÇO da
              estimativa, na mesma transação que confere o teto. Na
              última cota do dia o card virava "acabaram" enquanto a IA
              ainda estava olhando a foto — e agora que o pedido sai
              sozinho ao publicar, é exatamente aí que a pessoa está
              olhando pra tela. */}
          {podeEstimar && acabou && !estimando ? (
            <CotaDeCaloria contexto="prato" />
          ) : podeEstimar && (
            <div className="pad-botao">
              <button className="btn btn-primary" disabled={estimando}
                onClick={() => void pedirEstimativa()}>
                {estimando ? (semFoto ? "Lendo a descrição…" : "Olhando a foto…") : "✨ Estimar calorias"}
              </button>
              {/* O restante fica sob o botão, discreto: é informação pra
                  decidir, não aviso pra assustar. Some pra quem tem muito,
                  porque contar 97 de 100 não ajuda ninguém. */}
              {restam <= 3 && (
                <div className="cota-resta">
                  {restam === 1
                    ? "Resta 1 estimativa hoje"
                    : `Restam ${restam} estimativas hoje`}
                </div>
              )}
            </div>
          )}
        </div>
      );
    }

    const kcal = alvo.kcal;
    const meuPalpiteKcal = palpitesKcal.some((g) => g.uid === user?.uid);

    // Mesma mecânica do preço: o número real só aparece depois do chute.
    if (!meu && !meuPalpiteKcal) {
      return (
        <div className="card guess-card">
          <div className="guess-title">🔥 Quantas calorias você acha que tem?</div>
          <ChutarNumero
            placeholder="kcal"
            passo="10"
            onEnviar={(v) => {
              if (user) void guess(cid, pid, user.uid, "kcal", v);
            }}
            onAviso={setToast}
          />
          <div className="hint-row">
            O valor real só aparece depois do seu chute.{" "}
            {r.guessKcal > 0 && (
              <>Cravar vale <b>{pointsLabel(r.guessKcal)}</b>
                {r.tolKcal > 0 && ` (até ${r.tolKcal} kcal de margem)`}.</>
            )}
          </div>


        </div>
      );
    }

    const faixa = alvo.kcalMin != null && alvo.kcalMax != null && alvo.kcalMin !== alvo.kcalMax
      ? `entre ${formatKcal(alvo.kcalMin)} e ${formatKcal(alvo.kcalMax)} · ${FONTE[alvo.kcalSource ?? ""] ?? "estimado"}`
      : (FONTE[alvo.kcalSource ?? ""] ?? "estimado");

    return (
      <div className="card guess-card">
        <div className="guess-title">🔥 <strong>{formatKcal(kcal)}</strong></div>
        <div className="kcal-faixa">{faixa}</div>

        {!!alvo.kcalItems?.length && (
          <div className="kcal-itens">
            {alvo.kcalItems.map((i, n) => (
              <div key={n} className="kcal-item">
                <span>{i.nome}{i.porcao && <i> {i.porcao}</i>}</span>
                <b>{formatKcal(i.kcal)}</b>
              </div>
            ))}
          </div>
        )}

        <ListaDePalpites
          palpites={palpitesKcal} real={kcal} revelar={revelarPalpites}
          meuUid={user?.uid} nomeDe={nomeDe}
          formatar={formatKcal}
          distancia={(d) => Math.round(d).toLocaleString("pt-BR")}
        />

        {meu && (
          <>
            <div className="pad-botao">
              <CorrigirKcal atual={kcal}
                onEnviar={(v) => void corrigirCalorias(cid, pid, v)} />
            </div>
            <div className="hint-row">
              Estimativa de IA erra bastante. Sua correção vira a referência
              do grupo pra esse prato — é o que faz a base ficar melhor que a IA
              com o tempo.
            </div>
          </>
        )}
      </div>
    );
  })();

  return (
    <main className="screen">
      <TopBar
        back={`/c/${cid}`}
        action={
          /* O compartilhar é de todo mundo, não só do autor: a foto do
             prato alheio é o que a galera manda pro grupo. E ele existe
             porque o menu do navegador, que trazia "compartilhar imagem"
             de graça, foi desligado — ver `lib/nativo.ts`.

             O menu de três pontos também vale para todo mundo agora: no
             prato dos outros ele leva a denunciar e bloquear, que as
             lojas exigem que existam em todo conteúdo de gente. */
          <span className="topbar-acoes">
            {podeCompartilhar() && (
              <button className="topbar-btn" onClick={compartilhar} aria-label="Compartilhar">
                <IconShare />
              </button>
            )}
            <button className="topbar-btn" onClick={() => setMenu((v) => !v)} aria-label="Mais">
              <IconDots />
            </button>
          </span>
        }
      />

      <div className="screen-body no-tabbar sem-respiro">
        {/* A foto NÃO é cortada: `contain` sobre preto. Prato fotografado
            na vertical estava perdendo metade.

            Com mais de uma foto vira carrossel de rolagem horizontal com
            encaixe, não um slider com setas: é o gesto que todo mundo já
            faz, e funciona com o polegar sem mirar em botão. */}
        {semFoto ? (
          /* Prato sem foto: a descrição logo abaixo é o prato. O bloco
             segura o lugar da foto pra tela não abrir num título solto —
             e o toque longo continua abrindo a reação, como na foto. */
          <div className="post-hero-semfoto" {...segurarAFoto}>
            <span aria-hidden="true">📝</span>
            Postado sem foto
          </div>
        ) : (
        <div className="post-hero-wrap" {...segurarAFoto}>
          <div className="post-hero-trilho"
            onScroll={(e) => {
              const el = e.currentTarget;
              setNaFoto(Math.round(el.scrollLeft / Math.max(1, el.clientWidth)));
            }}>
            {(fotos.length ? fotos : [null]).map((f, i) => (
              <img key={i} className="post-hero" src={f ?? thumb ?? ""} alt={alvo.title}
                style={f ? undefined : { filter: "blur(6px)" }} />
            ))}
          </div>
          {fotos.length > 1 && (
            <div className="post-hero-pontos" aria-hidden="true">
              {fotos.map((_, i) => <span key={i} className={i === naFoto ? "on" : ""} />)}
            </div>
          )}
        </div>
        )}

        {menu && (
          <div className="card menu-flutuante list-card">
            {meu ? (
              <>
                <Link className="list-row" to={`/c/${cid}/p/${pid}/editar`}>
                  <span className="ico"><IconPencil size={22} /></span>
                  <span className="label">Editar prato</span>
                  <span className="chev"><IconChevron size={18} /></span>
                </Link>
                <button className="list-row danger" onClick={apagar}>
                  <span className="ico"><IconTrash size={22} /></span>
                  <span className="label">Apagar prato</span>
                </button>
              </>
            ) : (
              <>
                <button className="list-row" onClick={() => {
                  setMenu(false);
                  setDenuncia({
                    tipo: "prato", cid, pid, alvoUid: alvo.uid,
                    oQue: "este prato",
                  });
                }}>
                  <span className="ico"><IconFlag size={22} /></span>
                  <span className="label">Denunciar prato</span>
                </button>
                <button className="list-row danger" onClick={async () => {
                  setMenu(false);
                  if (!user) return;
                  if (!confirm(
                    `Bloquear ${alvo.authorName}? Os pratos e comentários dessa `
                    + "pessoa somem pra você. Ela não é avisada.")) return;
                  await bloquear(user.uid, alvo.uid, alvo.authorName);
                  navigate(`/c/${cid}`, { replace: true });
                }}>
                  <span className="ico"><IconBlock size={22} /></span>
                  <span className="label">Bloquear {alvo.authorName}</span>
                </button>
              </>
            )}
          </div>
        )}

        {/* A linha inteira do autor abre o perfil dele. Antes só o nome
            aparecia, sem levar a lugar nenhum — e tocar na foto de alguém
            é o gesto óbvio pra ver o perfil. */}
        <Link className="post-author" to={`/c/${cid}/u/${alvo.uid}`}>
          <Avatar name={alvo.authorName} photo={alvo.authorPhoto} size="md" />
          <div className="who">
            <div className="name">{alvo.authorName}</div>
            <div className="when">{quando(plateDate(alvo))}</div>
          </div>
          <span className="chev"><IconChevron size={18} /></span>
        </Link>

        <div className="post-title">{alvo.title || "Sem título"}</div>
        {alvo.description
          ? <div className="post-desc selecionavel">{alvo.description}</div>
          : <div style={{ height: 10 }} />}

        <div className="meta-chips">
          <span className="meta-chip">
            {alvo.homemade ? "👨‍🍳 Cozinhou" : "🛒 Comprou"}
            <b className="pts">{valeu > 0 ? `+${formatPoints(valeu)}` : "0"}</b>
          </span>
          {/* O custo é do CAMPO, como no placar: prato antigo sem foto não paga. */}
          {plate.semFoto === true && (
            <span className="meta-chip">
              📝 Sem foto <b className="pts menos">−{formatPoints(config.rules.semFotoCusto)}</b>
            </span>
          )}
          {canal && (
            <span className="meta-chip">
              {app && ["ifood", "99food", "keeta", "rappi", "aiqfome"].includes(app.id)
                ? <LogoDoApp app={app.id} /> : canal.emoji} {app ? app.rotulo : canal.rotulo}
            </span>
          )}
          {/* As missões de contagem/sequência em que este prato conta —
              é o "valeu a pena" de quem está tentando fechar uma. */}
          {missoes.map((m) => (
            <Link key={m.id} className="meta-chip tocavel" to={`/c/${cid}/missoes`}>
              🎯 {m.emoji} {m.name}
            </Link>
          ))}
          {cuisine && <span className="meta-chip">{cuisine.emoji} {cuisine.label}</span>}
          {meal && (
            <span className="meta-chip">
              {meal.emoji || "🍽️"} {meal.label}
              {alvo.mealRepeated && <i className="repetida">repetida</i>}
            </span>
          )}
          {/* O lugar é o único selo desta fila que leva a algum lugar, e por
              isso é `Link`: tocar no nome do restaurante é o gesto óbvio pra
              ver onde mais o grupo comeu, e era o único caminho que não
              existia — o guia só era alcançável por Detalhes. */}
          {(alvo.place || alvo.coords) && (
            <Link className="meta-chip tocavel" to={`/c/${cid}/guia`}>
              <IconPin size={16} /> {alvo.place || "Marcado no mapa"}
            </Link>
          )}
        </div>

        {/* Abaixo das marcas e acima da nota: a reação é a coisa mais
            barata de fazer no prato, então vem antes do que dá trabalho. */}
        <button className="reacao-abrir" onClick={() => setReagindo(true)}>
          {alvo.reactions && Object.keys(alvo.reactions).length
            ? <ResumoDeReacoes reactions={alvo.reactions} minha={minhaReacao} />
            : <span className="reacao-convite">Segure a foto pra reagir</span>}
        </button>

        {blocoNota}
        {blocoPreco}
        {blocoKcal}

        <Anuncio posicao="prato" />

        <div className="comentarios">
          {!conversaVisivel ? (
            <div className="comentarios-vazio">
              {fechado ? "Quem postou preferiu sem comentários." : "Este desafio está sem comentários e notas."}
            </div>
          ) : visiveis.length ? visiveis.map((c) => (
            <div key={c.id} className="comment">
              <Avatar name={c.name} photo={c.photo} size="md" />
              <div className="body">
                <div className="name">{c.name}</div>
                <div className="txt selecionavel">{c.text}</div>
                <div className="when">{c.at ? desde(c.at.toDate()) : "agora"}</div>
              </div>
            </div>
          )) : (
            <div className="comentarios-vazio">Ninguém comentou ainda.</div>
          )}
        </div>
        <div style={{ height: 12 }} />
      </div>

      {reagindo && (
        <BarraDeReacao
          minha={minhaReacao}
          aoEscolher={(e) => {
            setReagindo(false);
            if (!user) return;
            // A tela antecipa; a contagem chega quando a função devolver.
            const atual = minhaReacao;
            setMinhaReacao(atual === e ? null : e);
            void reagir(cid, pid, user.uid, e, atual);
          }}
          aoFechar={() => setReagindo(false)}
        />
      )}

      {denuncia && user && (
        <Denunciar quem={user.uid} alvo={denuncia} aoFechar={() => setDenuncia(null)} />
      )}

      {podeComentar && <div className="composer-bar">
        <Avatar name={me?.name ?? nomeDaSessao(user)} photo={me?.photo} size="md" />
        <input value={texto} maxLength={500} placeholder="Aa"
          onChange={(e) => setTexto(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") void enviarComentario(); }} />
        <button className={`send ${texto.trim() ? "on" : ""}`} onClick={enviarComentario}>
          Enviar
        </button>
      </div>}
      {conversaVisivel && !podeComentar && (
        <div className="composer-bar fechado">Neste desafio só o dono comenta (e quem postou responde).</div>
      )}
      {toast && <Toast text={toast} />}
    </main>
  );
}

/**
 * Quem chutou o quê.
 *
 * É o único lugar da tela que diz NOME, e por isso tem um piso. A nota é
 * anônima no banco, mas num prato com uma nota e um palpite só a única
 * pessoa visível vira a autora óbvia da nota — e com uma nota só, a média
 * exibida é a nota dela. Ver `podeRevelarPalpites`, no core.
 */
function ListaDePalpites(
  { palpites, real, revelar, meuUid, nomeDe, formatar, distancia }: {
    palpites: Guess[];
    real: number;
    revelar: boolean;
    meuUid: string | undefined;
    nomeDe: (uid: string) => string;
    formatar: (v: number) => string;
    /** A distância até o valor real, já em módulo. */
    distancia: (d: number) => string;
  },
) {
  // Sem palpite nenhum não há nome a esconder — e "ninguém chutou" diz
  // mais do que o aviso do piso diria.
  if (!palpites.length) {

    return <div className="hint-row">Ninguém chutou ainda.</div>;
  }

  if (!revelar) {
    const quantos = palpites.length === 1 ? "1 chute" : `${palpites.length} chutes`;
    return (
      <div className="hint-row">
        {`${quantos} — a lista aparece quando pelo menos ${MIN_PARTICIPANTES} pessoas participarem do prato.`}
      </div>
    );
  }

  const lista = [...palpites]
    .sort((a, b) => Math.abs(a.value - real) - Math.abs(b.value - real));
  // Empate na mesma distância leva o troféu junto: eleger um vencedor
  // único faria aparecer uma diferença que não existe.
  const campeoes = new Set(closestGuesses(lista, real).map((g) => g.uid));

  return (
    <div className="guess-list">
      {lista.map((g) => {
        const campeao = campeoes.has(g.uid);
        return (
          <div key={g.gid} className={`guess-item ${campeao ? "win" : ""}`}>
            <span className="who">{g.uid === meuUid ? "Você" : nomeDe(g.uid)}</span>
            <span className="val">{formatar(g.value)}</span>
            <span className="off">
              {g.awarded
                ? `🎯 +${formatPoints(g.awarded)}`
                : campeao
                  ? "🏆"
                  : `${g.value > real ? "+" : "−"}${distancia(Math.abs(g.value - real))}`}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/** Campo de chute com estado próprio, pra não redesenhar a tela a cada tecla. */
function ChutarPreco({ onEnviar, onAviso }: { onEnviar: (v: number) => void , onAviso?: (msg: string) => void}) {
  return <ChutarNumero placeholder="R$ —" passo="0.01" onEnviar={onEnviar} onAviso={onAviso} />;
}

function ChutarNumero(
  { onEnviar, placeholder, passo, onAviso }:
    {
      onEnviar?: (v: number) => void | Promise<void> | undefined;
      placeholder: string;
      passo: string;
      onAviso?: (msg: string) => void;

    },
) {

  const [valor, setValor] = useState("");
  const [indo, setIndo] = useState(false);
  const [confirmando, setConfirmando] = useState(false);
  const [valorParaConfirmar, setValorParaConfirmar] = useState<number | null>(null);


  const confirmarEnvio = () => {
    /* O teto e as mensagens são do core, iguais no app. Antes um valor
       inválido avisava e seguia em frente — faltava o `return` — e
       5.000.000.000 kcal chegava até a confirmação. */
    const lido = lerPalpite(placeholder === "kcal" ? "kcal" : "price", valor);
    if (!lido.ok) {
      onAviso?.(lido.erro);
      return;
    }
    const v = lido.valor;
    setValorParaConfirmar(v);
    setConfirmando(true);
  };

  const fecharConfirmacao = () => {
    setConfirmando(false);
    setValorParaConfirmar(null);
  };

  return (
    <>
      <div className="guess-row">
        <input type="number" inputMode="decimal" min="0" step={passo} placeholder={placeholder}
          max={TETO_DO_PALPITE[placeholder === "kcal" ? "kcal" : "price"]}
          value={valor} onChange={(e) => setValor(e.target.value)} />
        <button className="btn btn-primary" style={{ width: "auto", padding: "12px 22px" }}
          disabled={indo}
          onClick={confirmarEnvio}>
          Chutar
        </button>
      </div>

      {confirmando && valorParaConfirmar !== null && (
        <div className="guess-confirm-overlay" onClick={fecharConfirmacao}>
          <div className="guess-confirm-card" onClick={(e) => e.stopPropagation()}>
            <div className="guess-confirm-title">
              {placeholder === "kcal" ? "Confirmar calorias" : "Confirmar valor"}
            </div>
            <div className="guess-confirm-text">
              Você está enviando <strong>
                {placeholder === "kcal" ? formatKcal(valorParaConfirmar) : formatMoney(valorParaConfirmar)}
              </strong> para este prato?
            </div>
            <div className="guess-confirm-actions">
              <button className="btn btn-outline" onClick={fecharConfirmacao}>
                Cancelar
              </button>
              <button className="btn btn-primary" onClick={() => {
                if (!onEnviar) {
                  fecharConfirmacao();
                  return;
                }
                setIndo(true);
                fecharConfirmacao();
                void Promise.resolve(onEnviar(valorParaConfirmar)).finally(() => setIndo(false));
              }}>
                Enviar
              </button>
            </div>
          </div>

        </div>
      )}
    </>
  );
}

/** Corrigir o valor: começa como botão, vira campo quando tocado. */
function CorrigirKcal(
  { atual, onEnviar }: { atual: number; onEnviar: (v: number) => void },
) {
  const [aberto, setAberto] = useState(false);
  const [valor, setValor] = useState(String(Math.round(atual)));
  const [erro, setErro] = useState("");

  if (!aberto) {
    return (
      <button className="btn btn-white" onClick={() => setAberto(true)}>
        Corrigir o valor
      </button>
    );
  }
  return (
    <div className="guess-row" style={{ padding: 0 }}>
      <input type="number" inputMode="numeric" min="0" max={TETO_DO_PALPITE.kcal} step="10"
        value={valor} onChange={(e) => { setValor(e.target.value); setErro(""); }} autoFocus />
      <button className="btn btn-primary" style={{ width: "auto", padding: "12px 22px" }}
        onClick={() => {
          const lido = lerPalpite("kcal", valor);
          if (!lido.ok) { setErro(lido.erro); return; }
          setAberto(false);
          onEnviar(lido.valor);
        }}>
        Salvar
      </button>
      {erro && <div className="field-erro" role="alert">{erro}</div>}
    </div>
  );
}
